import store from '../../../../../store';
import ssFetch from '../../../../../modules/ssFetch';
import analytics from '../../../../../modules/analytics';
import { shareCurried, canShare } from '../../../../../modules/share';
import {
  getClientCount,
  updateSelfPromotionDates,
  LAST_INVITED_TO_BOOK,
} from '../../../../../modules/Api';
import { SITE_ROOT, APP } from '../../../../../config';
import { PRO_CLIENT_LIST } from '../../../../../route-names';

/**
 * Returns a function that either shares natively if on a device,
 * or failing that tries a desktop-friendly share.
 */
export async function getShareProviderCallback(
  providerId: number,
  email?: string,
): Promise<() => void | null> {
  // Share native if possible, otherwise open a mailto link
  const response = await ssFetch(`/feedback/pro/${providerId}`);
  const body = await response.json();
  const url = body?.rate_provider_link;

  if (!url) return null;

  const params = {
    url,
    title: 'Review me on StyleSeat!',
    text: 'Do you have a min to leave me a review on StyleSeat? Thanks!',
  };

  if (await canShare(params)) {
    // Generate synchronous share function
    return shareCurried(
      params,
      () => analytics.track('pro_reviewbuild_share_succeeded'),
      () => analytics.track('pro_reviewbuild_share_failed'),
    );
  }

  return () => {
    // non-native sharing, open an email
    const query = new URLSearchParams({
      subject: params.title,
      body: `${params.text} ${params.url}`,
    });
    const cleanedUpQuery = query.toString().replace(/\+/g, ' ');
    window.open(`mailto:${email}?${cleanedUpQuery}`, '_system');
  };
}

type Runnable = () => void;

export const getShareInviteClientToBook = async (
  providerId: number | string,
  vanityUrl: string,
  source: string,
): Promise<Runnable | null> => {
  const route = PRO_CLIENT_LIST;
  const params = {
    providerId,
    source,
  };
  const totalClientCount = await getClientCount(providerId);
  if (totalClientCount > 0) {
    return () => {
      store.dispatch.route.go({ route, params, notify: true });
    };
  }
  const proVanityUrl = APP ? `${SITE_ROOT}/m/v/${vanityUrl}` : `v/${vanityUrl}`;
  const shareParams = {
    text: 'Book your next appointment with me on StyleSeat. It\'s super easy!',
    url: proVanityUrl,
  };
  if (await canShare(shareParams)) {
    return shareCurried(
      shareParams,
      () => {
        // Native share with copy that relates to LAST_INVITED_TO_BOOK,
        // so record that date.
        updateSelfPromotionDates(providerId, [LAST_INVITED_TO_BOOK]);
        analytics.track('pro_onboarding_carousel_invite_to_book_shared_successfully');
      },
      err => analytics.track('pro_onboarding_carousel_invite_to_book_shared_fail', {
        reason: err,
      }),
    );
  }
  return () => {
    store.dispatch.route.go({ route, params, notify: true });
  };
};
